import React from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { Search, Sparkles, QrCode, Layers, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1758548157195-67d141468467?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwd29vZCUyMGludGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MTA3MDY4OHww&ixlib=rb-4.1.0&q=85";
const CRAFT = "https://images.unsplash.com/photo-1631396326838-de37e5f8bcbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxwbHl3b29kJTIwY3JhZnRzbWFuc2hpcCUyMGNhcnBlbnRyeXxlbnwwfHx8fDE3ODEwNzA2ODh8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-10 pt-8 md:pt-20 pb-12 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-8 md:gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 hed-fade-up">
            <div className="hed-overline">Heda Plywoods · AI product knowledge</div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight mt-4 leading-[1.02]">
              Every grain.
              <br />
              <span className="text-[#B5953B]">Every spec.</span>
              <br />
              On every phone.
            </h1>
            <p className="mt-5 md:mt-6 text-[15px] md:text-lg text-[#1A1A18]/80 max-w-xl leading-relaxed">
              Scan a Heda QR. Get the entire product — dimensions, finishes, installation,
              FAQs — and an AI assistant that answers, instantly. No PDFs to chase.
            </p>
            <div className="mt-8 md:mt-10 flex flex-wrap gap-3">
              <Link
                to="/search"
                data-testid="hero-search-cta"
                className="inline-flex items-center gap-2 bg-[#1A1A18] hover:bg-[#2a2a26] text-white px-6 py-3.5 rounded-sm font-medium transition-colors"
              >
                <Search size={16} strokeWidth={1.5} /> Browse the catalog
              </Link>
              <Link
                to="/admin/login"
                data-testid="hero-admin-cta"
                className="inline-flex items-center gap-2 border border-[#E8E6DF] hover:bg-[#F2F0EA] px-6 py-3.5 rounded-sm font-medium transition-colors"
              >
                Admin studio <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 hed-fade-up">
            <div className="relative">
              <img
                src={HERO}
                alt="Premium wood interior"
                className="w-full aspect-[4/5] object-cover border border-[#E8E6DF] rounded-sm"
              />
              <div className="absolute -bottom-6 -left-6 bg-white border border-[#E8E6DF] p-5 rounded-sm shadow-[0_8px_30px_rgba(26,26,24,0.06)] max-w-[220px] hidden sm:block">
                <div className="hed-dot mb-3" />
                <div className="font-serif text-lg leading-tight">
                  &ldquo;What thickness is this for kitchens?&rdquo;
                </div>
                <div className="text-[11px] text-[#5C5C54] mt-2">
                  — Real customer question, answered by Heda AI in 1.2s.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-[#E8E6DF] bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20">
          <div className="hed-overline">The pipeline</div>
          <h2 className="font-serif text-3xl md:text-5xl mt-3 max-w-2xl tracking-tight leading-tight">
            From a stack of brochures to a live product page — in minutes.
          </h2>
          <div className="grid md:grid-cols-3 gap-10 mt-14 hed-stagger">
            {[
              { i: "01", title: "Upload anything", icon: Layers, body: "PDF brochures, catalog scans, technical sheets, product photos." },
              { i: "02", title: "AI structures it", icon: Sparkles, body: "Hedaply extracts name, code, specs, sizes, finishes — every detail." },
              { i: "03", title: "Publish + QR + chat", icon: QrCode, body: "A premium product page goes live. Every print can carry a QR code linking to an AI assistant trained on that exact product." },
            ].map(({ i, title, icon: Icon, body }) => (
              <div key={i}>
                <div className="font-serif text-5xl text-[#B5953B]">{i}</div>
                <div className="flex items-center gap-2 mt-3">
                  <Icon size={16} strokeWidth={1.5} className="text-[#5E6033]" />
                  <h3 className="font-serif text-2xl">{title}</h3>
                </div>
                <p className="text-sm text-[#5C5C54] mt-3 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial split */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <img src={CRAFT} alt="Craft" className="w-full aspect-[4/3] object-cover rounded-sm border border-[#E8E6DF]" />
          <div>
            <div className="hed-overline">Built for thousands of SKUs</div>
            <h2 className="font-serif text-3xl md:text-5xl mt-3 tracking-tight leading-tight">
              You make great plywood.
              <br />
              <span className="text-[#5E6033]">We make it findable.</span>
            </h2>
            <p className="text-sm md:text-base text-[#1A1A18]/80 mt-5 leading-relaxed">
              Hedaply replaces hours of data entry with minutes of approval. AI does the reading.
              Your team does the deciding.
            </p>
            <Link
              to="/admin/login"
              className="inline-flex items-center gap-2 mt-8 bg-[#B5953B] hover:bg-[#9A7E32] text-white px-6 py-3 rounded-sm font-medium transition-colors"
              data-testid="footer-admin-cta"
            >
              Enter the studio <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
